<!-- Field Config Dashboard -->
<ng-template #filterIcon>
  <svg
    class="w-3 h-3 text-gray-400"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      d="M5.05 3C3.291 3 2.352 5.024 3.51 6.317l5.422 6.059v4.874c0 .472.227.917.613 1.2l3.069 2.25c1.01.742 2.454.036 2.454-1.2v-7.124l5.422-6.059C21.647 5.024 20.708 3 18.95 3H5.05Z"
    />
  </svg>
</ng-template>

<div class="h-full w-full min-h-0 flex flex-col gap-4 overflow-hidden">
  <!-- Header Section -->
  <div
    class="bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6 flex-shrink-0"
  >
    <div
      class="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start"
    >
      <div class="min-w-0">
        <h6
          class="leading-none text-2xl font-bold text-gray-900 dark:text-white pb-2"
        >
          Field Configuration
        </h6>
        <p class="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
          Manage which grower email accounts can access each field
        </p>
      </div>
    </div>
  </div>

  <!-- Table Section -->
  <div class="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6 flex-shrink-0"
    >
      <div
        class="flex flex-col gap-4 mb-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="min-w-0">
          <h6 class="text-lg font-semibold text-gray-900 dark:text-white">
            Field Config Rows
          </h6>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create, update, and remove field access records for grower emails
          </p>
          <div
            *ngIf="hasColumnFilters()"
            class="mt-3 flex flex-wrap items-center gap-2"
          >
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400"
              >Active Filters:</span
            >
            <span
              *ngIf="selectedFieldIdFilter"
              class="inline-flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-900 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-100"
            >
              {{ getFilterDisplayLabel('fieldId') }}: {{ selectedFieldIdFilter
              }}
              <button
                type="button"
                (click)="onSelectedColumnFilterChange('fieldId', null)"
              >
                &times;
              </button>
            </span>
            <span
              *ngIf="selectedEmailFilter"
              class="inline-flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-900 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-100"
            >
              {{ getFilterDisplayLabel('emailList') }}:
              <span class="lowercase">{{ selectedEmailFilter }}</span>
              <button
                type="button"
                (click)="onSelectedColumnFilterChange('emailList', null)"
              >
                &times;
              </button>
            </span>
            <button
              type="button"
              (click)="clearColumnFilters()"
              class="text-xs font-medium text-gray-700 hover:underline dark:text-gray-300"
            >
              Clear All
            </button>
          </div>
        </div>
        <div
          class="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto lg:justify-end"
        >
          <!-- Search Input -->
          <div class="w-full sm:flex-1 sm:max-w-md lg:min-w-[260px]">
            <div class="relative">
              <button
                *ngIf="searchTerm"
                type="button"
                (click)="searchTerm=''; onSearchChange()"
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white cursor-pointer"
                aria-label="Clear search"
              >
                <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
              </button>
            </div>
          </div>
          <!-- Action Buttons -->
          <div class="flex w-full gap-2 sm:w-auto">
            <button
              (click)="openAddModal()"
              type="button"
              class="w-full sm:w-auto justify-center uppercase text-sm font-semibold inline-flex items-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 px-4 py-2 transition-all duration-200"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 5v14m7-7H5"
                />
              </svg>
              ADD ROW
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Table -->
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-800 flex-1 min-h-0 flex flex-col overflow-hidden"
    >
      <div class="flex-1 overflow-auto p-1 md:p-2 field-config-scroll">
        <table
          class="w-full min-w-[760px] text-sm text-left text-gray-500 dark:text-gray-400"
        >
          <thead
            class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0"
          >
            <tr>
              <th scope="col" class="px-4 py-3 md:px-6">
                <div class="field-config-header-filter-row">
                  <button
                    type="button"
                    class="flex items-center gap-2"
                    (click)="onHeaderSort('Field ID')"
                  >
                    <span>Field ID</span>
                    <span class="text-sm leading-none">
                      <ng-container [ngSwitch]="getSortIcon('Field ID')">
                        <span *ngSwitchCase="'up'">&#9650;</span>
                        <span *ngSwitchCase="'down'">&#9660;</span>
                        <span *ngSwitchDefault>&#8597;</span>
                      </ng-container>
                    </span>
                  </button>
                  <div class="field-config-filter-control">
                    <app-dropdown
                      [overwriteStyle]="'field-config-filter-dropdown'"
                      [useSearch]="true"
                      [dropdownPlacement]="'bottom'"
                      [dropdownXPlacement]="'right'"
                      [customButtonTemplate]="filterIcon"
                      [options]="fieldIdFilterOptions"
                      [keyField]="'key'"
                      [labelField]="'label'"
                      [outputField]="'key'"
                      [selectedKey]="selectedFieldIdFilter"
                      (selectedKeyChange)="onSelectedColumnFilterChange('fieldId', $event)"
                    />
                  </div>
                </div>
              </th>
              <th scope="col" class="px-4 py-3 md:px-6">
                <div class="field-config-header-filter-row">
                  <span>Email List</span>
                  <div class="field-config-filter-control">
                    <app-dropdown
                      [overwriteStyle]="'field-config-filter-dropdown field-config-email-filter-dropdown'"
                      [useSearch]="true"
                      [dropdownPlacement]="'bottom'"
                      [dropdownXPlacement]="'right'"
                      [customButtonTemplate]="filterIcon"
                      [options]="emailFilterOptions"
                      [keyField]="'key'"
                      [labelField]="'label'"
                      [outputField]="'key'"
                      [selectedKey]="selectedEmailFilter"
                      (selectedKeyChange)="onSelectedColumnFilterChange('emailList', $event)"
                    />
                  </div>
                </div>
              </th>
              <th
                scope="col"
                class="px-4 py-3 md:px-6 cursor-pointer"
                (click)="onHeaderSort('Updated')"
              >
                <div class="flex items-center space-x-2">
                  <span>Last Updated</span>
                  <span class="text-sm leading-none">
                    <ng-container [ngSwitch]="getSortIcon('Updated')">
                      <span *ngSwitchCase="'up'">&#9650;</span>
                      <span *ngSwitchCase="'down'">&#9660;</span>
                      <span *ngSwitchDefault>&#8597;</span>
                    </ng-container>
                  </span>
                </div>
              </th>
              <th scope="col" class="px-4 py-3 md:px-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let row of paginatedRows; trackBy: trackByRowId"
              class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <th
                scope="row"
                class="px-4 py-4 md:px-6 text-gray-900 whitespace-nowrap dark:text-white align-top"
              >
                <div class="text-base font-semibold">{{ row.id }}</div>
              </th>
              <td class="px-4 py-4 md:px-6 align-top lowercase">
                <div class="flex flex-wrap gap-2">
                  <ng-container *ngIf="row.emailList.length > 0; else noEmails">
                    <span
                      *ngFor="let email of row.emailList"
                      class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {{ email }}
                    </span>
                  </ng-container>
                  <ng-template #noEmails>
                    <span class="text-xs text-gray-400">No emails</span>
                  </ng-template>
                </div>
              </td>
              <td class="px-4 py-4 md:px-6 align-top whitespace-nowrap">
                {{ formatDateTime(row.updatedAt) }}
              </td>
              <td class="px-4 py-4 md:px-6 align-top">
                <div class="flex flex-wrap gap-2">
                  <button
                    (click)="openEditModal(row)"
                    class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200 dark:text-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:focus:ring-blue-400"
                  >
                    <svg
                      class="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    (click)="openDeleteModal(row)"
                    class="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-all duration-200 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:focus:ring-red-400"
                  >
                    <svg
                      class="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Empty State -->
        <div *ngIf="filteredRows.length === 0" class="text-center py-8">
          <svg
            class="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 10h16M4 14h16M4 18h16"
            />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No config rows found
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ searchTerm ? 'Try adjusting your search text.' : 'Create your
            first row to get started.' }}
          </p>
        </div>
      </div>
      <!-- Pagination Controls -->
      <div
        *ngIf="paginatedRows.length > 0"
        class="flex flex-col gap-3 px-4 py-3 md:px-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm text-gray-700 dark:text-gray-300">Show:</span>
          <select
            [(ngModel)]="itemsPerPage"
            (ngModelChange)="changeItemsPerPage()"
            class="text-sm border border-gray-300 rounded px-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option
              *ngFor="let option of itemsPerPageOptions"
              [ngValue]="option"
            >
              {{ option }}
            </option>
          </select>
          <span class="text-sm text-gray-700 dark:text-gray-300">per page</span>
        </div>

        <div class="text-sm text-gray-700 dark:text-gray-300">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{
          Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems
          }} rows
        </div>

        <div class="flex flex-wrap items-center gap-1">
          <button
            (click)="goToPage(currentPage - 1)"
            [disabled]="currentPage === 1"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Previous
          </button>

          <button
            *ngFor="let page of getVisiblePages()"
            (click)="page !== -1 && goToPage(page)"
            [class]="
              page === currentPage
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
            "
            class="px-3 py-1 text-sm border border-gray-300 rounded-md dark:border-gray-600"
            [disabled]="page === -1"
          >
            {{ page === -1 ? '...' : page }}
          </button>

          <button
            (click)="goToPage(currentPage + 1)"
            [disabled]="currentPage === totalPages"
            class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Add Row Modal -->
  <div
    *ngIf="isAddModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-lg mx-4 overflow-visible"
    >
      <div
        class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Add Field Config Row
        </h3>
        <button
          (click)="closeModals()"
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>
      <div class="p-4 md:p-5">
        <form class="space-y-4">
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Field ID *
            </label>
            <app-dropdown
              [overwriteStyle]="'field-config-modal-dropdown'"
              [useSearch]="true"
              [dropdownPlacement]="'bottom'"
              [dropdownXPlacement]="'right'"
              [options]="addFieldIdOptions"
              [keyField]="'key'"
              [labelField]="'label'"
              [outputField]="'key'"
              [selectedKey]="newRow.id || null"
              [searchPlaceholder]="'Search field ID'"
              [borderless]="false"
              (selectedKeyChange)="onModalFieldIdChange('new', $event)"
            />
            <p *ngIf="newRowErrors.fieldId" class="mt-1 text-xs text-red-600">
              {{ newRowErrors.fieldId }}
            </p>
          </div>

          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Email List
            </label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                [(ngModel)]="newRowEmailInput"
                name="new_email_input"
                placeholder="Add email and press Enter"
                (keydown.enter)="addEmailToRow('new'); $event.preventDefault()"
                class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              />
              <button
                type="button"
                (click)="addEmailToRow('new')"
                class="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <p *ngIf="newRowErrors.emailList" class="mt-1 text-xs text-red-600">
              {{ newRowErrors.emailList }}
            </p>
            <div class="flex flex-wrap gap-2 mt-3">
              <span
                *ngFor="let email of newRow.emailList"
                class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              >
                {{ email }}
                <button
                  type="button"
                  (click)="removeEmailFromRow('new', email)"
                  class="ml-2 text-blue-700 hover:text-blue-900 dark:text-blue-200"
                >
                  &times;
                </button>
              </span>
            </div>
          </div>
        </form>
      </div>
      <div
        class="flex flex-col gap-3 p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600 sm:flex-row sm:items-center"
      >
        <button
          (click)="addRow()"
          type="button"
          class="w-full sm:w-auto text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        >
          Add Row
        </button>
        <button
          (click)="closeModals()"
          type="button"
          class="w-full sm:w-auto sm:ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
  <!-- Edit Row Modal -->
  <div
    *ngIf="isEditModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-lg mx-4 overflow-visible"
    >
      <div
        class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Edit Field Config Row
        </h3>
        <button
          (click)="closeModals()"
          type="button"
          class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <svg class="w-3 h-3" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <div class="p-4 md:p-5">
        <form class="space-y-4">
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Field ID *
            </label>
            <app-dropdown
              [overwriteStyle]="'field-config-modal-dropdown'"
              [useSearch]="true"
              [dropdownPlacement]="'bottom'"
              [dropdownXPlacement]="'right'"
              [options]="editFieldIdOptions"
              [keyField]="'key'"
              [labelField]="'label'"
              [outputField]="'key'"
              [selectedKey]="editRow.id || null"
              [searchPlaceholder]="'Search field ID'"
              [disabled]="true"
              (selectedKeyChange)="onModalFieldIdChange('edit', $event)"
            />
            <p *ngIf="editRowErrors.fieldId" class="mt-1 text-xs text-red-600">
              {{ editRowErrors.fieldId }}
            </p>
          </div>
          <div>
            <label
              class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Email List
            </label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                [(ngModel)]="editRowEmailInput"
                name="edit_email_input"
                placeholder="Add email and press Enter"
                (keydown.enter)="addEmailToRow('edit'); $event.preventDefault()"
                class="text-sm rounded-lg block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              />
              <button
                type="button"
                (click)="addEmailToRow('edit')"
                class="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <p
              *ngIf="editRowErrors.emailList"
              class="mt-1 text-xs text-red-600"
            >
              {{ editRowErrors.emailList }}
            </p>
            <div class="flex flex-wrap gap-2 mt-3">
              <span
                *ngFor="let email of editRow.emailList"
                class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              >
                {{ email }}
                <button
                  type="button"
                  (click)="removeEmailFromRow('edit', email)"
                  class="ml-2 text-blue-700 hover:text-blue-900 dark:text-blue-200"
                >
                  &times;
                </button>
              </span>
            </div>
          </div>
        </form>
      </div>
      <div
        class="flex flex-col gap-3 p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600 sm:flex-row sm:items-center"
      >
        <button
          (click)="updateRow()"
          type="button"
          class="w-full sm:w-auto text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        >
          Save Changes
        </button>
        <button
          (click)="closeModals()"
          type="button"
          class="w-full sm:w-auto sm:ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
  <!-- Delete Row Modal -->
  <div
    *ngIf="isDeleteModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-lg shadow dark:bg-gray-700 w-full max-w-md mx-4"
    >
      <div class="p-6 text-center">
        <svg
          class="mx-auto mb-4 text-red-400 w-12 h-12 dark:text-red-300"
          fill="none"
          viewBox="0 0 20 20"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
        <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-100">
          Delete
          <strong class="text-red-600">{{ selectedRow?.id }}</strong>?
        </h3>
        <p class="text-sm text-gray-400 dark:text-gray-100 mb-6">
          This will remove the grower email access record for this field.
        </p>
        <div
          class="flex flex-col gap-3 sm:flex-row sm:justify-center sm:space-x-3"
        >
          <button
            (click)="confirmDelete()"
            type="button"
            class="w-full sm:w-auto text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center justify-center px-5 py-2.5 text-center"
          >
            Yes, delete
          </button>
          <button
            (click)="closeModals()"
            type="button"
            class="w-full sm:w-auto text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
